import React, { useState, useRef, useEffect, useCallback } from "react";
import { PreviousUpload, Upload, UploadOptions } from "@maysoft/tus-js-client";
import { Tooltip, FormLabel, IconButton, FormControl, FormHelperText, LinearProgress } from "@mui/material";
import { Clear as ClearIcon, Close as CloseIcon, Pause as PauseIcon, Photo as PhotoIcon, PlayArrow as PlayArrowIcon } from "@mui/icons-material";

import nanoid from "../../lib/nanoid";
import Helpers from "../../commons/helpers";
import ResumableUploadService, { ResumableUploadRequest } from "../../services/asset/resumableUpload.service";

import { Constants } from "../../constants/constants";
import { Method } from "../../services/base/HttpClient";
import { FileAccessMode, FileProcessingStatus } from "../../constants/enums";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import useStrings from "../../hooks/useStrings";

interface UploadFileResponse {
    id: string;
    accessUrl: string;
}

interface IProps {
    label?: string;
    fileId?: string;
    width?: number;
    height?: number;
    folderPath?: string;
    className?: string;
    required?: boolean;
    errorMessage?: string;
    limitFileSize?: number;
    hiddenLoading?: boolean;
    disabled?: boolean;
    optimize?: boolean;
    accessMode?: FileAccessMode;
    imageRatio?: "1:1" | "2:1" | "3:2" | "4:3" | "16:9";
    onChangeImage: (res?: UploadFileResponse) => void;
}

interface ExtendedPreviousUpload extends PreviousUpload {
    uploadUrl: string;
    urlStorageKey: string;
}

const ImageUploader: React.FC<IProps> = (props: IProps) => {
    const strings = useStrings();
    const context = useCommonComponentContext();

    const imageInputId = nanoid();
    const tusUpload = useRef<Upload>();
    const tusUploadUrl = useRef<string>();
    const imageInputRef = useRef<any>(null);
    const imageRatio = props.imageRatio || "2:1";

    const [pausing, setPausing] = useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [uploadProgress, setUploadProgress] = React.useState(0);
    const [originalFileId, setOriginalFileId] = useState<string>();
    const [errorMessage, setErrorMessage] = React.useState(props.errorMessage || "");

    /* Default 2:1 */
    let thumbnailWidth = props?.width || 300;
    let thumbnailHeight = props?.height || 150;
    switch (imageRatio) {
        case "1:1":
            thumbnailWidth = thumbnailHeight;
            break;

        case "3:2":
            thumbnailHeight = thumbnailWidth / (3 / 2);
            break;

        case "4:3":
            thumbnailHeight = thumbnailWidth / (4 / 3);
            break;

        case "16:9":
            thumbnailHeight = thumbnailWidth / (16 / 9);
            break;

        default:
            break;
    }

    useEffect(() => {
        if (!Helpers.isNullOrEmpty(props.fileId) && props.fileId !== originalFileId) {
            setOriginalFileId(props.fileId);
        }
    }, [props.fileId]);

    const uploadFile = async (file: File) => {
        let validFile = true;
        setUploading(false);
        setUploadProgress(0);
        setErrorMessage("");

        if (Helpers.isNumber(props.limitFileSize) && file.size > props.limitFileSize) {
            validFile = false;
            setErrorMessage(strings.format(strings.Common.FILE_SIZE_MUST_SMALLER_THAN, Helpers.bytesToSize(props.limitFileSize)));
        }

        if (!validFile) {
            imageInputRef.current.value = null;
            props.onChangeImage(undefined);
            return;
        }

        try {
            const request: ResumableUploadRequest = {
                tenantCode: context.tenantCode,
                serviceCode: context.serviceCode,
                accessMode: !Helpers.isNullOrEmpty(props?.accessMode)
                    ? `${props.accessMode}`
                    : `${FileAccessMode.Private}`,
                processingStatus: (props.optimize === false)
                    ? `${FileProcessingStatus.Processed}`
                    : `${FileProcessingStatus.Unprocessed}`,
            };

            if (!Helpers.isNullOrEmpty(props?.folderPath)) {
                request.folderPath = props.folderPath;
            }

            const options: UploadOptions = {
                onBeforeRequest: (req) => {
                    if (Helpers.isNullOrEmpty(tusUploadUrl.current) && req.getMethod() === Method.PATCH) {
                        tusUploadUrl.current = req.getURL();
                    }
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = parseInt(((bytesUploaded / bytesTotal) * 100).toFixed(2));
                    setUploadProgress(percentage);
                },
                onSuccess: (res) => {
                    let id = "";
                    if (res.getHeader("asset-id")) {
                        id = res.getHeader("asset-id");
                    }

                    let accessUrl = "";
                    if (res.getHeader("asset-url")) {
                        accessUrl = res.getHeader("asset-url");
                    }

                    let error = "";
                    if (res.getHeader("asset-error")) {
                        error = res.getHeader("asset-error");
                        setErrorMessage(error);
                        handleUploadError();
                    }

                    const response = {
                        id,
                        error,
                        accessUrl,
                    };

                    tusUpload.current = undefined;
                    tusUploadUrl.current = undefined;
                    props.onChangeImage(response);
                    setOriginalFileId(response.id);
                    setUploading(false);
                },
                onError: (error) => {
                    handleUploadError();
                },
            };

            tusUpload.current = ResumableUploadService.upload(file, request, options);

            setUploading(true);
            setPausing(false);
            setOriginalFileId(undefined);
            startOrResumeUpload(tusUpload.current);
        } catch (error) {
            if (tusUpload.current) {
                tusUpload.current.abort();
            }
            handleUploadError();
        }
    };

    const startOrResumeUpload = (tusUpload: Upload) => {
        tusUpload
            .findPreviousUploads()
            .then((allPreviousUploads) => {
                for (let index = 0; index < allPreviousUploads.length; index++) {
                    const previousUpload = allPreviousUploads[index] as ExtendedPreviousUpload;
                    if (previousUpload.uploadUrl === tusUploadUrl.current) {
                        tusUpload.resumeFromPreviousUpload(allPreviousUploads[index]);
                        break;
                    }
                }
                tusUpload.start();
            })
            .catch((error) => {
                // console.log("Error when start/resume upload:", error);
            });
    };

    const terminateUpload = () => {
        // Abort upload first, then remove upload key in localStorage
        if (tusUpload.current) {
            tusUpload.current.abort();
            tusUpload.current
                .findPreviousUploads()
                .then((allPreviousUploads) => {
                    for (let index = 0; index < allPreviousUploads.length; index++) {
                        const previousUpload = allPreviousUploads[index] as ExtendedPreviousUpload;
                        if (previousUpload.uploadUrl === tusUploadUrl.current) {
                            localStorage.removeItem(previousUpload.urlStorageKey);
                            break;
                        }
                    }
                    tusUploadUrl.current = undefined;
                })
                .catch((error) => {
                    // console.log("Error when abort upload:", error);
                });
        }

        // Make http request to terminate upload
        if (tusUploadUrl.current) {
            Upload.terminate(tusUploadUrl.current).catch((error) => {
                // console.log("Error when terminate upload:", error);
            });
        }
    };

    const handleContinueOrPauseUpload = useCallback(() => {
        if (tusUpload.current && uploadProgress < 100) {
            if (pausing) {
                setPausing(false);
                startOrResumeUpload(tusUpload.current);
            } else {
                setPausing(true);
                tusUpload.current.abort();
            }
        }
    }, [pausing, uploadProgress]);

    const handleTerminateUpload = useCallback(() => {
        if (uploadProgress < 100) {
            setPausing(true);
            tusUpload.current && tusUpload.current.abort();
            Helpers.showConfirmAlert(
                strings.Common.CANCEL_UPLOAD_IMAGE,
                () => {
                    terminateUpload();
                    imageInputRef.current.value = null;
                    props.onChangeImage(undefined);
                    setOriginalFileId(undefined);
                    setUploading(false);
                },
                () => {
                    setPausing(false);
                    tusUpload.current && startOrResumeUpload(tusUpload.current);
                },
                strings.Common.YES,
                strings.Common.NO
            );
        }
    }, [uploadProgress, strings]);

    const handleUploadError = () => {
        if (imageInputRef.current) {
            imageInputRef.current.value = null;
            setUploading(false);
            setUploadProgress(0);
            setOriginalFileId(undefined);
            setErrorMessage(strings.Common.UPLOAD_IMAGE_FAILED);
        }
        props.onChangeImage(undefined);
    };

    const handleRemove = () => {
        imageInputRef.current.value = null;
        setOriginalFileId(undefined);
        props.onChangeImage(undefined);
    };

    return (
        <FormControl
            disabled={props.disabled}
            className={props.className}
            style={{ width: "fit-content" }}
            error={!Helpers.isNullOrEmpty(props.errorMessage) || !Helpers.isNullOrEmpty(errorMessage)}
        >
            {props.label && (
                <FormLabel style={{ marginBottom: 8, fontSize: "0.875rem", fontWeight: "400" }}>
                    {props.required ? `${props.label} *` : props.label}
                </FormLabel>
            )}
            <div
                style={{
                    borderRadius: 6,
                    width: thumbnailWidth,
                    height: thumbnailHeight,
                    backgroundColor: "#e5e7eb",
                }}
                className="d-flex justify-content-center align-items-center"
            >
                {originalFileId ? (
                    <div style={{ position: "relative" }}>
                        <label id={imageInputId + "Label"} htmlFor={imageInputId} style={{ marginBottom: 0, cursor: "pointer" }}>
                            <img
                                alt="uploader"
                                style={{
                                    width: thumbnailWidth,
                                    height: thumbnailHeight,
                                    objectFit: "cover",
                                }}
                                src={Helpers.getFileAccessUrl(originalFileId)}
                            />
                        </label>
                        <IconButton
                            onClick={handleRemove}
                            disabled={props.disabled}
                            sx={{ p: 1, position: "absolute", top: 0, right: 0 }}
                        >
                            <CloseIcon color="secondary" />
                        </IconButton>
                    </div>
                ) : uploading ? (
                    !props.hiddenLoading && (
                        <div className="icon-block px-2 w-100 h-100 bg-grey-100 d-flex flex-column justify-content-center align-items-center">
                            <div className="w-100 d-flex justify-content-center align-items-center">
                                <LinearProgress className="mr-1" variant="determinate" style={{ width: "75%", height: 7 }} value={uploadProgress} />

                                <div style={{ width: "25%" }}>
                                    <Tooltip arrow title={strings.Common.CANCEL} aria-label={strings.Common.CANCEL} placement="top">
                                        <IconButton size="small" onClick={handleTerminateUpload}>
                                            <ClearIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip
                                        arrow
                                        title={pausing ? strings.Common.CONTINUE : strings.Common.PAUSE}
                                        aria-label={pausing ? strings.Common.CONTINUE : strings.Common.PAUSE}
                                        placement="top"
                                    >
                                        <IconButton size="small" onClick={handleContinueOrPauseUpload}>
                                            {pausing ? <PlayArrowIcon /> : <PauseIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>

                            <div style={{ fontSize: 18 }} className="w-100">
                                {`Trạng thái: ${uploadProgress}%`}
                            </div>
                        </div>
                    )
                ) : (
                    <Tooltip title={"Chọn ảnh"} aria-label={"Chọn ảnh"} arrow placement="right">
                        <label
                            id={imageInputId + "Label"}
                            htmlFor={imageInputId}
                            className="icon-block w-100 bg-grey-100 d-flex flex-column justify-content-center align-items-center"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",

                                width: "100%",
                                height: "100%",
                                cursor: "pointer",
                            }}
                        >
                            <PhotoIcon />
                        </label>
                    </Tooltip>
                )}
            </div>

            <input
                hidden
                ref={imageInputRef}
                id={imageInputId}
                type="file"
                accept="image/*"
                disabled={props.disabled}
                onChange={(event) => {
                    if (event.target.files?.[0]) {
                        uploadFile(event.target.files[0]);
                    }
                }}
            />

            <FormHelperText>{props.errorMessage || errorMessage}</FormHelperText>
        </FormControl>
    );
};

export default ImageUploader;
